import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Last updated: March 7, 2026</p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
          <p className="text-gray-300 leading-relaxed">
            Welcome to Pulse Media ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
            you have a positive experience when using our streaming service. This Privacy Policy explains how we 
            collect, use, disclose, and safeguard your information when you use our mobile application and website.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
          
          <h3 className="text-xl font-medium mb-2 text-cyan-400">Personal Information</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            When you create an account, we collect:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
            <li>Email address</li>
            <li>Username</li>
            <li>Password (encrypted)</li>
            <li>Profile information you choose to provide</li>
          </ul>

          <h3 className="text-xl font-medium mb-2 text-cyan-400">Payment Information</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            For subscription and pay-per-view purchases, payment processing is handled securely through 
            Google Play Store, Apple App Store, or Stripe. We do not store your full credit card details.
          </p>

          <h3 className="text-xl font-medium mb-2 text-cyan-400">Camera and Media</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            Our app requests camera access solely for the purpose of recording video reviews. 
            Video reviews you submit are uploaded to our servers and may be displayed publicly 
            alongside the content you reviewed. You have full control over whether to submit a video review.
          </p>

          <h3 className="text-xl font-medium mb-2 text-cyan-400">Usage Data</h3>
          <p className="text-gray-300 leading-relaxed">
            We automatically collect information about how you interact with our service, including:
          </p>
          <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
            <li>Content you watch and viewing history</li>
            <li>Search queries</li>
            <li>Device information and identifiers</li>
            <li>IP address and location data</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-300 leading-relaxed mb-4">We use the information we collect to:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>Provide and maintain our streaming service</li>
            <li>Process your subscriptions and payments</li>
            <li>Personalize your content recommendations</li>
            <li>Send you updates, notifications, and promotional materials (with your consent)</li>
            <li>Improve our service and develop new features</li>
            <li>Detect and prevent fraud or unauthorized access</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. Information Sharing</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            We do not sell your personal information. We may share your information with:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li><strong>Service Providers:</strong> Third parties who help us operate our service (hosting, analytics, payment processing)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
          <p className="text-gray-300 leading-relaxed">
            We implement appropriate technical and organizational measures to protect your personal information 
            against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
            over the Internet is 100% secure.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
          <p className="text-gray-300 leading-relaxed mb-4">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>Access and receive a copy of your personal data</li>
            <li>Correct inaccurate personal data</li>
            <li>Request deletion of your personal data</li>
            <li>Object to or restrict processing of your data</li>
            <li>Withdraw consent at any time</li>
            <li>Cancel your subscription at any time</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. Children's Privacy</h2>
          <p className="text-gray-300 leading-relaxed">
            Our service is not intended for children under 13 years of age. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and believe your 
            child has provided us with personal information, please contact us.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. Changes to This Policy</h2>
          <p className="text-gray-300 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
          <p className="text-gray-300 leading-relaxed">
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <p className="text-cyan-400 mt-2">contact@pulsemedia.vip</p>
        </section>

        <div className="border-t border-gray-800 pt-8 mt-8">
          <p className="text-gray-500 text-sm">
            © 2026 Pulse Media. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
